import {Component, inject, ChangeDetectionStrategy} from '@angular/core';
import {BadgeComponent} from "../../../shared-module/components/badge/badge.component";
import {ButtonComponent} from "../../../shared-module/components/button/button.component";
import LoaderService from "../../../shared-module/services/loader.service";
import {AuthService} from "../../../shared-module/services/auth.service";
import {ApplicationConfig, config} from "../../../../config";
import {Router, RouterLink} from "@angular/router";
import {DatePipe, SlicePipe} from "@angular/common";
import Invoice from "../../../shared-module/model/invoice.model";
import Relation from "../../../shared-module/model/relation.model";
import {CurrencyComponent} from "../../../shared-module/components/currency/currency.component";
import {PaymentStatusComponent} from "../../../shared-module/components/payment-status/payment-status.component";
import {InvoiceModalComponent} from "../../../shared-module/components/invoice-modal/invoice-modal.component";
import ModalService from "../../../shared-module/services/modal.service";
import {Subscription as rxjsSubscription} from "rxjs";
import Subscription from "../../../shared-module/model/subscription.model"
import {TicketStateLabel} from "../../../shared-module/enums/ticket-state-label";
import {Ticket} from "../../../shared-module/model/ticket.model";
import {TimeAgoPipe} from "../../../shared-module/pipes/time-ago.pipe";
import {CountIndicatorComponent} from "../../../shared-module/components/count-indicator/count-indicator.component";
import AddressPipe from "../../../shared-module/pipes/address.pipe";
import {SubscriptionState} from "../../../shared-module/enums/subscription-state";
import {SubscriptionStateLabel} from '../../../shared-module/enums/subscription-state-label';
import {CardComponent} from "../../../shared-module/components/card/card.component";
import {sortSubscriptions} from "../../../shared-module/functions/sortSubscriptions.function";
import {downloadInvoice} from "../../../shared-module/functions/downloadInvoice.function";

@Component({
    selector: 'app-home',
    imports: [
    BadgeComponent,
    ButtonComponent,
    RouterLink,
    CurrencyComponent,
    DatePipe,
    PaymentStatusComponent,
    TimeAgoPipe,
    CountIndicatorComponent,
    SlicePipe,
    AddressPipe,
    CardComponent
],
    templateUrl: './home.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './home.component.scss'
})
export class HomeComponent {
  readonly loaderService: LoaderService = inject(LoaderService);
  protected readonly authService: AuthService = inject(AuthService);
  protected readonly router: Router = inject(Router);
  private readonly modalService: ModalService = inject(ModalService);

  public config: ApplicationConfig = config;
  public currentRelation: Relation | null = null;

  protected readonly TicketStateLabel: Record<string, string> = TicketStateLabel;
  protected readonly Relation: typeof Relation = Relation;
  protected readonly SubscriptionStateLabel: { [key in SubscriptionState]: string } = SubscriptionStateLabel;

  private modal: rxjsSubscription | null = null;

  constructor() {
    this.authService.getUser(['relations.tickets', 'relations.subscriptions', 'relations.invoices.lines']).subscribe();

    this.authService.getActiveRelation().subscribe((relation: Relation | null): void => {
      if (!relation) {
        return;
      }

      this.currentRelation = relation;
    });
  }

  protected get latestInvoice(): Invoice | null {
    if (!this.currentRelation) {
      return null;
    }

    const invoices: Invoice[] = this.currentRelation.invoices ?? [];
    if (invoices.length === 0) {
      return null;
    }

    return invoices.reduce(function (a: Invoice, b: Invoice) {
      return a.invoice_date > b.invoice_date ? a : b;
    });
  }

  public showLoader(): void {
    this.loaderService.show();
    setTimeout(() => {
      this.loaderService.hide();
    }, 5000);
  }

  public showInvoice(invoice: Invoice): void {
    if (this.modal) {
      return;
    }
    this.modal = this.modalService.open(InvoiceModalComponent, invoice, {
      width: '900px',
      showCloseButton: true,
      title: 'Factuur: ' + invoice.invoice_number,
      badgeIcon: 'file-lines'
    }).subscribe(() => {
      this.modal?.unsubscribe();
      this.modal = null;
    });
  }

  protected get recentTickets(): Ticket[] {
    if (!this.currentRelation) {
      return [];
    }

    //create a copy of the array for sorting
    const recent_tickets: Ticket[] = [...this.currentRelation.tickets];
    if (!recent_tickets) {
      return [];
    }

    recent_tickets.sort((a: Ticket, b: Ticket) => {
      return a.updated_at > b.updated_at ? -1 : 1;
    });

    if (this.config.recentTicketsCount) {
      return recent_tickets.slice(0, this.config.recentTicketsCount);
    }

    return recent_tickets;
  }

  protected get maxTicketsForRelationReached(): boolean {
    // return false if there are no tickets or if the current relation is not set
    if (!this.currentRelation || !this.currentRelation.tickets) {
      return false;
    }

    return (this.currentRelation?.tickets.length >= config.maxTicketsPerRelation)
  }

  public download(invoice: Invoice): void {
    downloadInvoice(invoice);
  }

  protected get subscriptions(): Subscription[] {
    return sortSubscriptions(this.currentRelation?.subscriptions);
  }
}
